<template>
  <div class="level">
    <div
      class="level__wrapper"
      :style="isViewingLevelNote && 'background-color: var(--gray)'"
    >
      <LevelTitle :title="title" />
      <div class="level__details-wrapper">
        <Actions
          @toggleVideo="toggleVideo"
          @toggleQuiz="toggleQuiz"
          @toggleExercise="toggleExercise"
          :content="{ video, quiz, exercises }"
          :isContentEngaged="{
            video: isWatching,
            quiz: isTakingQuiz,
            exercises: exercisesOpen,
          }"
          :isLoading="isLoading"
          :isUser="hasAccess"
        />
        <LevelControl
          @click="toggleVideo()"
          :isLoading="isLoading"
          :hasAccess="hasAccess || isVideoFree()"
          :isContentActive="isWatching"
          :statusText="stateText"
          :status="videoStatus"
          :plansLink="plansLink"
          :courseStatus="courseStatus"
        />
      </div>
      <NoteIndicator
        v-show="levelBookmarks && levelBookmarks.length && hasAccess"
        class="level__note-indicator"
        fill="var(--yellow)"
      />
    </div>
    <div class="level__content">
      <Content
        :video="video"
        :quizId="quiz && quiz.id ? quiz.id : ''"
        :exercises="exercises"
        :isTakingExercises="exercisesOpen"
        :videoStatus="videoStatus"
        :isWatching="isWatching"
        :isLoading="isLoading"
        :levelTitle="title"
        :isTakingQuiz="isTakingQuiz"
        :quizStatus="quizStatus"
        :courseId="courseId"
        :nextVideo="nextVideo"
        :isUser="hasAccess"
        @isLoading="setLoader"
      />
    </div>
  </div>
</template>

<script>
import { isFreeContent } from '@/helpers'
import { mapState, mapGetters } from 'vuex'
import Actions from '@/components/course/content/level/Actions.vue'
import Content from '@/components/course/content/level/Content.vue'
import NoteIndicator from '@/assets/icons/course/note_indicator.svg'
import CONSTANTS from '@/constants'
import LevelTitle from './level/LevelTitle.vue'
import LevelControl from './level/LevelControl.vue'

export default {
  name: 'VideoLevel',
  data() {
    return {
      isLoading: false,
      exercisesOpen: false,
    }
  },
  components: {
    LevelTitle,
    LevelControl,
    Actions,
    Content,
    NoteIndicator: { ...NoteIndicator, name: 'NoteIndicator' },
  },
  props: {
    courseId: {
      type: String,
      required: true,
    },
    courseStatus: {
      type: String,
      required: true,
    },
    exercises: {
      type: Array,
      required: false,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    nextVideo: {
      type: Object,
      required: true,
    },
    plansLink: {
      type: String,
      required: true,
    },
    quiz: {
      type: Object,
      required: false,
    },
    quizStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    video: {
      type: Object,
      required: true,
    },
    videoStatus: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState({
      playingVideoId: state => state.content.playingVideoId,
      currentQuizId: state => state.content.currentQuizId,
      currentHoveredNote: state => state.content.currentHoveredNote,
    }),
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    isTakingQuiz() {
      if (this.quiz) {
        return this.quiz.id === this.currentQuizId
      }
      return false
    },
    isViewingLevelNote() {
      if (this.levelBookmarks && this.levelBookmarks.length) {
        const ids = this.levelBookmarks.map(bookmark => bookmark.video)
        return ids.includes(this.currentHoveredNote)
      }
      return false
    },
    isWatching() {
      return this.video.id === this.playingVideoId
    },
    levelBookmarks() {
      return this.courseBookmarks.filter(
        bookmark => bookmark.video === this.video.id
      )
    },
    stateText() {
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        return 'Resume'
      }
      if (this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        return 'Replay'
      }
      return 'Play'
    },
  },
  methods: {
    async toggleVideo() {
      this.isLoading = true
      const { playingVideoId, video } = this
      if (playingVideoId === video.id) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setPlayingVideoTime', 0)
      } else {
        this.$store.dispatch('content/setPlayingVideoTime', 0)
        this.$store.dispatch('content/setPlayingVideoId', video.id)
      }
      this.exercisesOpen = false
      this.isLoading = false
    },
    async toggleQuiz() {
      this.isLoading = true
      const { currentQuizId, quiz } = this
      if (currentQuizId === quiz.id) {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', '')
      } else {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', quiz.id)
      }
      this.exercisesOpen = false
      this.isLoading = false
    },
    async toggleExercise() {
      this.exercisesOpen = !this.exercisesOpen
      this.$store.dispatch('content/setPlayingVideoId', '')
      this.$store.dispatch('content/setCurrentQuizId', '')
    },
    toggleNextContent() {
      const { nextVideo } = this
      if (nextVideo) {
        this.$store.dispatch('content/setPlayingVideoId', nextVideo.uuid)
        this.$store.dispatch('content/setPlayingVideoTime', 0)
      }
    },
    isVideoFree() {
      return isFreeContent(this.video)
    },
    setLoader(state) {
      this.isLoading = state
    },
  },
}
</script>
